// Shared utilities and constants for Admin components
import { 
  Users, Shield, Settings as SettingsIcon, Plus, Edit, Trash2, Key, Save, X,
  Mail, FileText, ExternalLink, Eye, EyeOff, Copy, Check, AlertCircle,
  Paperclip, Tag, Info, Download, Upload, Database, Phone, Briefcase,
  UserCog, RefreshCw, FileSpreadsheet, Table, Cloud
} from 'lucide-react';

// Available template tags
export const templateTags = [
  { tag: '{{empresa_nombre}}', label: 'Nombre de Empresa', category: 'empresa' },
  { tag: '{{empresa_email}}', label: 'Email de Empresa', category: 'empresa' },
  { tag: '{{empresa_telefono}}', label: 'Teléfono de Empresa', category: 'empresa' },
  { tag: '{{empresa_categoria}}', label: 'Categoría de Empresa', category: 'empresa' },
  { tag: '{{contacto_nombre}}', label: 'Nombre del Contacto', category: 'contacto' },
  { tag: '{{contacto_email}}', label: 'Email del Contacto', category: 'contacto' },
  { tag: '{{contacto_telefono}}', label: 'Teléfono del Contacto', category: 'contacto' },
  { tag: '{{contacto_posicion}}', label: 'Posición del Contacto', category: 'contacto' },
  { tag: '{{usuario_nombre}}', label: 'Tu Nombre', category: 'usuario' },
  { tag: '{{usuario_email}}', label: 'Tu Email', category: 'usuario' },
  { tag: '{{usuario_telefono}}', label: 'Tu Teléfono', category: 'usuario' },
  { tag: '{{usuario_posicion}}', label: 'Tu Posición', category: 'usuario' },
  { tag: '{{usuario_rol}}', label: 'Tu Rol', category: 'usuario' },
  { tag: '{{asignado_nombre}}', label: 'Nombre del Asignado', category: 'asignado' },
  { tag: '{{asignado_email}}', label: 'Email del Asignado', category: 'asignado' },
  { tag: '{{asignado_telefono}}', label: 'Teléfono del Asignado', category: 'asignado' },
  { tag: '{{asignado_posicion}}', label: 'Posición del Asignado', category: 'asignado' },
  { tag: '{{fecha_actual}}', label: 'Fecha Actual', category: 'sistema' },
];

// Phone format helper
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

export const handlePhoneInput = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 10) {
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  return value.slice(0, 14);
};

// Available permissions
export const availablePermissions = [
  { id: 'all', label: 'Acceso Total', description: 'Control completo del sistema' },
  { id: 'view_all', label: 'Ver Todo', description: 'Ver todos los datos del CRM' },
  { id: 'view_own', label: 'Ver Propios', description: 'Ver solo datos asignados' },
  { id: 'edit_all', label: 'Editar Todo', description: 'Editar cualquier dato' },
  { id: 'edit_own', label: 'Editar Propios', description: 'Editar solo datos asignados' },
  { id: 'edit_team', label: 'Editar Equipo', description: 'Editar datos del equipo supervisado' },
  { id: 'delete_tasks', label: 'Eliminar Tareas', description: 'Puede eliminar tareas' },
  { id: 'delete_empresas', label: 'Eliminar Empresas', description: 'Puede eliminar empresas' },
  { id: 'delete_notes', label: 'Eliminar Notas', description: 'Puede eliminar notas' },
  { id: 'delete_deals', label: 'Eliminar Deals', description: 'Puede eliminar deals de embudos' },
  { id: 'manage_users', label: 'Gestionar Usuarios', description: 'Crear y editar usuarios' },
  { id: 'manage_pipelines', label: 'Gestionar Embudos', description: 'Crear y editar embudos' },
  { id: 'reports', label: 'Ver Reportes', description: 'Acceso a reportes avanzados' },
  { id: 'admin', label: 'Administración', description: 'Acceso al panel de admin' },
];

// API Key service suggestions
export const apiKeySuggestions = [
  { name: 'SendGrid', key: 'sendgrid', description: 'Servicio de envío de emails', url: 'https://sendgrid.com/docs/ui/account-and-settings/api-keys/', instructions: 'Ve a Settings > API Keys > Create API Key' },
  { name: 'Resend', key: 'resend', description: 'Alternativa moderna para emails', url: 'https://resend.com/api-keys', instructions: 'Crea una cuenta en resend.com y genera una clave' },
  { name: 'Google Calendar', key: 'google_calendar', description: 'Integración con calendario', url: 'https://console.cloud.google.com/apis/credentials', instructions: 'Habilita Calendar API y genera credenciales OAuth 2.0' },
  { name: 'Twilio', key: 'twilio', description: 'SMS y comunicaciones', url: 'https://console.twilio.com/', instructions: 'Obtén tu Account SID y Auth Token' },
  { name: 'OpenAI', key: 'openai', description: 'IA y generación de texto', url: 'https://platform.openai.com/api-keys', instructions: 'Crea una nueva API key en platform.openai.com' },
  { name: 'n8n', key: 'n8n', description: 'Automatización de workflows', url: 'https://docs.n8n.io/api/', instructions: 'Ve a Settings > API > Create API Key en tu instancia de n8n' },
  { name: 'Gemini (Google AI)', key: 'gemini', description: 'IA de Google', url: 'https://aistudio.google.com/app/apikey', instructions: 'Genera una API key en Google AI Studio' },
  { name: 'Gamma AI', key: 'gamma_ai', description: 'Generador de propuestas y presentaciones con IA', url: 'https://gamma.app/settings/api', instructions: 'Ve a Settings > API Keys en gamma.app (requiere plan Pro o superior)' },
  { name: 'Meta (Facebook/Instagram)', key: 'meta', description: 'APIs de Meta para Facebook e Instagram', url: 'https://developers.facebook.com/apps/', instructions: 'Crea una app en Meta for Developers y obtén el Access Token' },
  { name: 'LinkedIn', key: 'linkedin', description: 'APIs de LinkedIn', url: 'https://www.linkedin.com/developers/apps', instructions: 'Crea una app en LinkedIn Developers y obtén el Client ID y Secret' },
  { name: 'YouTube', key: 'youtube', description: 'APIs de YouTube', url: 'https://console.cloud.google.com/apis/credentials', instructions: 'Habilita YouTube Data API v3 y genera credenciales' },
  { name: 'TikTok', key: 'tiktok', description: 'APIs de TikTok', url: 'https://developers.tiktok.com/', instructions: 'Registra tu app en TikTok for Developers' }
];

// Export icons for use in subcomponents
export const Icons = {
  Users, Shield, SettingsIcon, Plus, Edit, Trash2, Key, Save, X,
  Mail, FileText, ExternalLink, Eye, EyeOff, Copy, Check, AlertCircle,
  Paperclip, Tag, Info, Download, Upload, Database, Phone, Briefcase,
  UserCog, RefreshCw, FileSpreadsheet, Table, Cloud
};
